'use client';

import { ANALYTICS_CONSENT_KEY } from '@/lib/constants';
import { ELocalStorageKeys, ESessionStorageKeys } from '@web-store-platforms/types';
import { BaseWebStorageService } from '@web-store-platforms/storage';

class PortalWebStorageService extends BaseWebStorageService {
  constructor() {
    super({
      onError: (key, error) => {
        console.error(`Error reading from storage (${key}):`, error);
      },
    });
  }

  // Both of these reach a storage object directly rather than through the base
  // class, so they need their own guard: on a document that refuses storage the
  // property access itself throws, and these run where a throw costs the render.
  getAny = (key: ELocalStorageKeys | ESessionStorageKeys): any => {
    if (!this.isClient) return null;

    try {
      return window.localStorage.getItem(key)
        ? JSON.parse(window.localStorage.getItem(key)!)
        : window.sessionStorage.getItem(key)
          ? JSON.parse(window.sessionStorage.getItem(key)!)
          : null;
    } catch (error) {
      console.error(`Error reading from storage (${key}):`, error);

      return null;
    }
  };

  clear = (): void => {
    if (!this.isClient) return;
    const overrideStore = this.get(ELocalStorageKeys.OVERRIDE_STORE);
    const integrationProfile = this.get(ELocalStorageKeys.INTEGRATION_PORFILE);
    const currentStoreId = this.get(ELocalStorageKeys.CURRENT_STORE_ID);
    const portalId = this.get(ELocalStorageKeys.PORTAL_ID);
    const loginConsent = this.get(ELocalStorageKeys.USER_LOGIN_TERMS_CONSENT);
    const currentLanguage = this.get(ELocalStorageKeys.CURRENT_LANGUAGE);

    let analyticsConsent: string | null = null;
    try {
      analyticsConsent = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
      window.localStorage.clear();
    } catch (error) {
      // Nothing was stored, so there is nothing to clear and nothing to restore;
      // a logout must still complete.
      console.error('Error clearing storage:', error);

      return;
    }

    this.set(ELocalStorageKeys.OVERRIDE_STORE, overrideStore);
    this.set(ELocalStorageKeys.INTEGRATION_PORFILE, integrationProfile);
    this.set(ELocalStorageKeys.CURRENT_STORE_ID, currentStoreId);
    this.set(ELocalStorageKeys.PORTAL_ID, portalId);
    this.set(ELocalStorageKeys.USER_LOGIN_TERMS_CONSENT, loginConsent);
    this.set(ELocalStorageKeys.CURRENT_LANGUAGE, currentLanguage);
    if (analyticsConsent !== null) {
      window.localStorage.setItem(ANALYTICS_CONSENT_KEY, analyticsConsent);
    }
  };
}

export const webStorageUtil = new PortalWebStorageService();
